import { useAtomValue, useSetAtom } from 'jotai';
import { extractApiErrorMessage } from 'lib/helper';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select, { StylesConfig } from 'react-select';
import { CircleSpinner } from 'react-spinners-kit';
import {
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { companyIdAtom } from 'views/state';

import {
  bulkAssignBPAFindings,
  bulkRemoveBPAFindingAssignments,
  fetchBPAAssigneeOptions,
} from '../../services/bpaFindingsService';
import { findingsDataRefreshAtom, selectedRowIdsAtom } from '../../state';
import { BPAAssigneeOption, BPAFinding } from '../../types/bpaFindings.types';

interface BPAAssigneeModalProps {
  finding?: BPAFinding;
  isOpen: boolean;
  toggle: () => void;
  isBulk?: boolean;
}

const getMemberLabel = (member: BPAAssigneeOption): string =>
  `${member.first_name} ${member.last_name} (${member.email})`;

const detailItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.5rem',
  marginBottom: '0.5rem',
};

const detailLabelStyle: React.CSSProperties = {
  flex: '0 0 95px',
  maxWidth: '95px',
};

const detailValueStyle: React.CSSProperties = {
  flex: '1 1 auto',
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const BPAAssigneeModal: React.FC<BPAAssigneeModalProps> = ({
  finding,
  isOpen,
  toggle,
  isBulk = false,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const selectedFindingIds = useAtomValue(selectedRowIdsAtom);
  const refreshFindingsGridData = useSetAtom(findingsDataRefreshAtom);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [members, setMembers] = useState<BPAAssigneeOption[]>([]);
  const [assignee, setAssignee] = useState<BPAAssigneeOption | null>(null);
  const [dueDate, setDueDate] = useState<Date | null>(null);

  const findingIds = isBulk
    ? selectedFindingIds
    : finding?.id
      ? [finding.id]
      : [];

  useEffect(() => {
    if (!isOpen || !companyId || companyId === -1) return;

    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError('');

      try {
        const fetchedMembers = await fetchBPAAssigneeOptions(companyId);
        if (cancelled) return;

        setMembers(fetchedMembers);

        if (!isBulk && finding?.assignee_user_id) {
          const currentAssignee = fetchedMembers.find(
            (member) => member.users_id === finding.assignee_user_id,
          );
          setAssignee(currentAssignee ?? null);
        } else {
          setAssignee(null);
        }

        setDueDate(
          !isBulk && finding?.due_date ? new Date(finding.due_date) : null,
        );
      } catch (err) {
        if (!cancelled) {
          const message = extractApiErrorMessage(
            err,
            'Failed to load assignee options.',
          );
          setError(message);
          showFailureToast(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [companyId, finding, isBulk, isOpen]);

  const handleSave = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    isAssigning: boolean,
  ): Promise<void> => {
    event.preventDefault();

    if (!companyId || companyId === -1) {
      showFailureToast('Please select a company before assigning findings.');
      return;
    }

    if (findingIds.length === 0) {
      showFailureToast('No BPA findings selected.');
      return;
    }

    if (isAssigning && !assignee) {
      showFailureToast('Please select an assignee.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = isAssigning
        ? await bulkAssignBPAFindings({
            company_id: companyId,
            finding_ids: findingIds,
            assignee_id: assignee!.users_id,
            ...(dueDate ? { due_date: dueDate.toISOString() } : {}),
          })
        : await bulkRemoveBPAFindingAssignments({
            company_id: companyId,
            finding_ids: findingIds,
          });

      if (response.status === 200 || response.status === 201) {
        const count = findingIds.length;
        showSuccessToast(
          isAssigning
            ? `Successfully assigned ${count} BPA finding${
                count === 1 ? '' : 's'
              } to ${assignee?.first_name} ${assignee?.last_name}`
            : `Successfully unassigned ${count} BPA finding${
                count === 1 ? '' : 's'
              }`,
        );
        refreshFindingsGridData(new Date().toISOString());
        toggle();
      } else {
        throw new Error(
          `Failed to ${isAssigning ? 'assign' : 'unassign'} BPA findings.`,
        );
      }
    } catch (err) {
      const message = extractApiErrorMessage(
        err,
        `Failed to ${isAssigning ? 'assign' : 'unassign'} BPA findings.`,
      );
      setError(message);
      showFailureToast(message);
    } finally {
      setLoading(false);
    }
  };

  const selectStyles: StylesConfig<BPAAssigneeOption, false> = {
    control: (base) => ({
      ...base,
      cursor: 'pointer',
    }),
    option: (base) => ({
      ...base,
      cursor: 'pointer',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  const renderFormFields = () => (
    <>
      <Row className="form-group">
        <Col xs="auto" className="d-flex align-items-center">
          <label>
            <strong>Assignee</strong>
          </label>
        </Col>
        <Col>
          <Select
            value={assignee}
            onChange={(selected) =>
              setAssignee(selected as BPAAssigneeOption | null)
            }
            getOptionLabel={getMemberLabel}
            getOptionValue={(option) => String(option.users_id)}
            placeholder="Select Assignee"
            isClearable
            menuPortalTarget={document.body}
            styles={selectStyles}
            options={members}
          />
        </Col>
      </Row>
      <Row className="form-group">
        <Col xs="auto" className="d-flex align-items-center">
          <label>
            <strong>Due Date (Optional)</strong>
          </label>
        </Col>
        <Col>
          <DatePicker
            selected={dueDate}
            onChange={(date) => setDueDate(date)}
            timeFormat="HH:mm"
            dateFormat="MM-dd-yyyy HH:mm"
            showTimeInput
            isClearable
            className="form-control"
            placeholderText="Select Due Date"
            minDate={new Date()}
            allowSameDay={true}
            wrapperClassName="datePicker"
            popperPlacement="bottom"
            popperProps={{
              strategy: 'fixed',
            }}
          />
        </Col>
      </Row>
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="md">
      <ModalHeader toggle={toggle}>Assign/Unassign BPA Findings</ModalHeader>
      <ModalBody>
        {isBulk ? (
          <Row>
            <Col md="12">
              <strong>{selectedFindingIds.length}</strong> findings selected
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md="12">
              <div style={detailItemStyle}>
                <strong style={detailLabelStyle}>Finding:</strong>
                <span style={detailValueStyle}>{finding?.description}</span>
              </div>
            </Col>
            <Col md="12">
              <div style={detailItemStyle}>
                <strong style={detailLabelStyle}>Finding ID:</strong>
                <span style={detailValueStyle}>{finding?.id}</span>
              </div>
            </Col>
            <Col md="12">
              <div style={detailItemStyle}>
                <strong style={detailLabelStyle}>Integration:</strong>
                <span style={detailValueStyle}>
                  {finding?.integration_integration_label}
                </span>
              </div>
            </Col>
          </Row>
        )}

        {isBulk ? (
          <Row>
            <Col>
              <Card>
                <CardBody className="mt-3">
                  <p>
                    Select the assignee and due date for the selected findings.
                    Or click Unassign to remove the assignments.
                    <br />
                    <br />
                    Note: If selected findings are already assigned, the
                    association will be updated to this assignee.
                  </p>
                  {renderFormFields()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        ) : (
          renderFormFields()
        )}

        {error && <div className="text-danger">{error}</div>}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="secondary"
          onClick={(event) => handleSave(event, false)}
          disabled={loading || findingIds.length === 0}
          className="mr-2"
        >
          Unassign
        </Button>
        {loading ? (
          <CircleSpinner size={20} color="#0665a9" loading={true} />
        ) : (
          <Button
            color="primary"
            onClick={(event) => handleSave(event, true)}
            disabled={loading || !assignee || findingIds.length === 0}
          >
            Assign
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default BPAAssigneeModal;
